import * as _ from 'lodash';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import type { FunctionComponent, VoidFunctionComponent } from 'react';
import type { InjectedRouter } from 'react-router';
import type { SetRequired } from 'type-fest';

import Locator from '../../../../shared/Locator';
import { useDispatchOnMount } from '../../../hooks';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import { nuc } from '../../../utils';
import { SecurityRequired } from '../../generic';
import Subnav from '../../generic/Subnav';
import { SidebarLayout } from '../../layouts';
import PackageInventoryFilters from '../filters';
import { PackageVersionFiltersPackagesContainer } from '../filters/PackageFiltersForVersion';
import { PackageInventoryFiltersProjectVersionContainer as PackageInventoryProjectFilters } from '../filters/ProjectFilters';
import useSyncVersionDetailsStateAndURLParams from '../hooks/useSyncVersionDetailsStateAndURLParams';
import type { RouterProps } from '../../../types';
import type { PackageInventoryParams } from '../PackageInventoryContainer';

import PackageProjectsTable from './PackageProjectsTable';
import PackageVersionCTA from './PackageVersionCTA';

type NavKey = 'projects' | 'vulnerabilities';

function isNavKey(value: string | undefined): value is NavKey {
  return value === 'projects' || value === 'vulnerabilities';
}

const SubNav: VoidFunctionComponent<{
  navKey: NavKey;
  packageLocator: string;
  version: string;
}> = ({ navKey, packageLocator, version }) => {
  const packageProjectAsyncStatus = useSelector(PackageInventoryStore.selectors.getAsyncStatusGetPackageProjects);
  const versionDetailsStateQueryString = useSelector(PackageInventoryStore.selectors.getVersionDetailsStateQueryString);
  const packageProjectCount = useSelector(PackageInventoryStore.selectors.getTotalPackageProjectsCount);
  const packageVulnerabilitiesAsyncStatus = useSelector(
    PackageInventoryStore.selectors.getAsyncStatusGetPackageVulnerabilities,
  );
  const packageVulnerabilitiesCount = useSelector(PackageInventoryStore.selectors.getVulnerabilitiesCount);
  const { fetcher } = Locator.parse(packageLocator);
  const shouldDisplayPackageVulns = fetcher !== 'csbinary';

  return (
    <Subnav activeKey={navKey}>
      <Subnav.ItemWithCount
        to={`/packages/${nuc(packageLocator)}/${version}/projects?${versionDetailsStateQueryString}`}
        navKey="projects"
        isLoading={packageProjectAsyncStatus.isLoading}
        count={packageProjectCount}
      >
        Projects
      </Subnav.ItemWithCount>
      { shouldDisplayPackageVulns &&
      <Subnav.ItemWithCount
        to={`/packages/${nuc(packageLocator)}/${version}/vulnerabilities?${versionDetailsStateQueryString}`}
        navKey="vulnerabilities"
        isLoading={packageVulnerabilitiesAsyncStatus.isLoading}
        count={packageVulnerabilitiesCount}
      >
        Vulnerabilities
      </Subnav.ItemWithCount> }
    </Subnav>
  );
};

type ProjectsTableProps = {
  params: {
    packageLocator: string;
    version: string;
  };
};

export const ProjectsTable: VoidFunctionComponent<ProjectsTableProps> = ({ params }) => {
  return (
    <div className="container padding-top-lg" data-test-id="package-inventory-package-version">
      <SidebarLayout>
        <SidebarLayout.Main>
          <PackageProjectsTable packageLocator={params.packageLocator} version={params.version} />
        </SidebarLayout.Main>
        <SidebarLayout.Sidebar>
          <PackageInventoryFilters>
            <PackageVersionFiltersPackagesContainer params={params} />
            <PackageInventoryProjectFilters params={params} />
          </PackageInventoryFilters>
        </SidebarLayout.Sidebar>
      </SidebarLayout>
    </div>
  );
};

type PackageVersionContainerProps = RouterProps<SetRequired<PackageInventoryParams, 'packageLocator' | 'version'>> & {
  router: InjectedRouter;
};

const PackageVersionContainer: FunctionComponent<PackageVersionContainerProps> = ({
  location,
  router,
  params,
  children,
}) => {
  const maybeNavKey = _.last(location.pathname.split('/'));
  const navKey: NavKey = isNavKey(maybeNavKey) ? maybeNavKey : 'projects';
  useDispatchOnMount(PackageInventoryStore.actions.resetVersionDetailsPage);
  // Synchronize URL query parameters and Redux state.
  useSyncVersionDetailsStateAndURLParams({ location, router, params });
  useDispatchOnMount(PackageInventoryStore.actions.fetchPackageVulnerabilities, params.packageLocator, params.version);

  return (
    <>
      <Helmet
        title={`${params.packageLocator}$${params.version}`}
        titleTemplate="%s - Package Inventory | FOSSA"
        defaultTitle="Package Inventory | FOSSA"
      />
      <PackageVersionCTA params={params} />
      <SecurityRequired>
        <SubNav navKey={navKey} packageLocator={params.packageLocator} version={params.version} />
      </SecurityRequired>
      {children}
    </>
  );
};

export default PackageVersionContainer;
